import { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../components/navigation/AuthenticationStateManager";
import api from "../../api/axiosInstance";
import ResumePreview from "./ResumePreview";
import { setResumeFile } from "../../resumeFileStore";


export default function ResumeUpload() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [fileName, setFileName] = useState("");
  const [jobDescription, setJobDescription] = useState("");
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [selectedRole, setSelectedRole] = useState("Data Engineer");
  const [analysisResult, setAnalysisResult] = useState(null);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/resume-builder");
    }
  }, [isAuthenticated, navigate]);

  /* FILE CHANGE */
  const handleFileChange = (e) => {
    if (e.target.files.length > 0) {
      const file = e.target.files[0];
      setFileName(file.name);
    }
  };

  /* ANALYZE */
  const handleAnalyze = async () => {
    try {
      if (!fileInputRef.current?.files[0]) {
        alert("Please upload a resume first");
        return;
      }

      if (jobDescription.length < 300) {
        alert("Job description must be at least 300 characters");
        return;
      }

      setIsAnalyzing(true);

      // ✅ Declare file FIRST before using it
      const file = fileInputRef.current.files[0];

      // ✅ Store file in memory store (no base64, no quota issues)
      setResumeFile(file);
      sessionStorage.setItem("resume_file_name", file.name);

      const formData = new FormData();
      formData.append("resume", file);
      formData.append("jobDescription", jobDescription);
      formData.append("jobRole", selectedRole);

      localStorage.setItem("job_role", selectedRole);
      localStorage.setItem("job_description", jobDescription);

      const isImprovedFlag = localStorage.getItem("isImproved");
// Also detect by filename — improved PDFs are named like "Loganathan_classic_re..."
      const isImprovedByFilename = /(_classic_|_modern_|_dark_|_elegant_|improved_|_ats_)/i.test(file.name);
      const isImproved = isImprovedFlag === "true" || isImprovedByFilename;

      console.log("🔥 isImproved FLAG:", isImproved, "| filename hint:", isImprovedByFilename);

      const endpoint = isImproved
         ? "/api/resume/analyze-improved"
         : "/api/resume/analyze";

      console.log("🚀 CALLING API:", endpoint);

      const res = await api.post(endpoint, formData);
      const raw = res.data;
      console.log("🔍 FULL RAW RESPONSE:", JSON.stringify(raw, null, 2));

      localStorage.removeItem("isImproved");

     const isImprovedResponse = raw.ats_score !== undefined;

const structuredData = {
  overallScore:    raw.overallScore    ?? raw.original_ats_score ?? 0,
  jobMatchScore:   raw.jobMatchScore   ?? raw.job_match_score    ?? 0,
  improvedScore:   raw.ats_score       ?? raw.overallScore       ?? 0,

  // ✅ Use tier fields from improved response, fallback to direct fields
  atsParsing:      raw.atsParsing      ?? (isImprovedResponse ? (raw.ats_tier === "HIGH" || raw.ats_tier === "MEDIUM" ? "PASS" : "FAIL") : "FAIL"),
  jobMatch:        raw.jobMatch        ?? raw.match_tier         ?? "LOW",
  recruiterImpact: raw.recruiterImpact ?? raw.ats_tier           ?? "LOW",

  parsedData:      raw.parsedData      || {},
  matchedSkills:   raw.matchedSkills   ?? raw.matched_skills     ?? [],
  missingSkills:   raw.missingSkills   ?? raw.missing_skills     ?? [],
  strengths:       raw.strengths       || [],
  quickWins:       raw.quickWins       ?? raw.quick_wins         ?? [],
  summaryVerdict:  raw.summaryVerdict  ?? raw.summary_verdict    ?? "",
  preview:         raw.preview         || {},
  fullReport:      raw.fullReport      || {},
};
      console.log("🔥 raw.parsedData:", raw.parsedData);
      console.log("🔥 structuredData:", structuredData);

      // ✅ Save full data to localStorage for ResumeAnalysis page
     localStorage.setItem("resume_analysis_data", JSON.stringify({
  overallScore:    raw.overallScore    ?? raw.original_ats_score ?? 0,
  jobMatchScore:   raw.jobMatchScore   ?? raw.job_match_score    ?? 0,
  improvedScore:   raw.ats_score       ?? null,
  atsParsing:      raw.atsParsing      ?? (isImprovedResponse ? (raw.ats_tier !== "LOW" ? "PASS" : "FAIL") : "FAIL"),
  jobMatch:        raw.jobMatch        ?? raw.match_tier         ?? "LOW",
  recruiterImpact: raw.recruiterImpact ?? raw.ats_tier           ?? "LOW",
  parsedData:      raw.parsedData      || {},
  matchedSkills:   raw.matchedSkills   ?? raw.matched_skills     ?? [],
  missingSkills:   raw.missingSkills   ?? raw.missing_skills     ?? [],
  strengths:       raw.strengths       || [],
  quickWins:       raw.quickWins       ?? raw.quick_wins         ?? [],
  summaryVerdict:  raw.summaryVerdict  ?? raw.summary_verdict    ?? "",
  fullReport:      raw.fullReport      || {},
  keywords:        raw.keywords        || {},
  role:            selectedRole,
}));
      // ✅ Also save resume text if available
      if (raw.parsedData?.rawText) {
        localStorage.setItem("resume_original_text", raw.parsedData.rawText);
      }

      setAnalysisResult(structuredData);
      setShowPreview(true);

    } catch (err) {
      console.error("❌ Analyze Error:", err);
      alert("Resume analysis failed");
    } finally {
      setIsAnalyzing(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-2xl p-10 -mt-20 relative z-10">
      <h2 className="text-3xl font-bold text-center mb-2">Check Your Resume</h2>
      <p className="text-center text-gray-500 mb-8">
        We'll analyze ATS compatibility, job match, and recruiter impact.
      </p>

      <input
        type="file"
        accept=".pdf,.doc,.docx"
        hidden
        ref={fileInputRef}
        onChange={handleFileChange}
      />

      <div
        onClick={() => fileInputRef.current.click()}
        className="border-2 border-dashed border-gray-300 rounded-xl p-10 text-center cursor-pointer hover:border-blue-500 transition"
      >
        <p className="text-gray-500 mb-2">Drag & drop your resume</p>
        <p className="text-sm text-gray-400">PDF, DOC, DOCX</p>
        <button type="button" className="mt-4 bg-black text-white px-5 py-2 rounded-lg">
          Upload Resume
        </button>
      </div>

      {fileName && (
        <p className="text-center mt-4 text-sm text-gray-600">
          Uploaded: <strong>{fileName}</strong>
        </p>
      )}

      <div className="mt-8">
        <label className="block font-medium mb-2">Target Job Role</label>
        <select
          value={selectedRole}
          onChange={(e) => setSelectedRole(e.target.value)}
          className="border rounded-lg p-3 w-full"
        >
          <option>Data Engineer</option>
          <option>Software Engineer</option>
          <option>Backend Developer</option>
          <option>Frontend Developer</option>
          <option>Data Analyst</option>
          <option>Product Manager</option>
        </select>
      </div>

      {fileName && (
        <div className="mt-6">
          <textarea
            rows={6}
            placeholder="Paste full job description (minimum 300 characters)"
            value={jobDescription}
            onChange={(e) => setJobDescription(e.target.value)}
            className="border p-4 rounded-lg w-full"
          />
          <button
            disabled={jobDescription.length < 300 || isAnalyzing}
            onClick={handleAnalyze}
            className="w-full mt-4 bg-blue-600 text-white py-3 rounded-lg font-semibold disabled:opacity-50"
          >
            {isAnalyzing ? "AI is analyzing your resume..." : "Analyze Resume"}
          </button>
        </div>
      )}

      {showPreview && analysisResult && (
        <ResumePreview
          score={analysisResult.improvedScore || analysisResult.overallScore}
          role={selectedRole}
          atsParsing={analysisResult.atsParsing}
          jobMatch={analysisResult.jobMatch}
          recruiterImpact={analysisResult.recruiterImpact}
          previewImprovements={analysisResult.preview?.improvements}
          onDownload={() => {
            if (!isAuthenticated) {
              navigate("/authentication-portal");
            } else {
              navigate("/candidate/resume/editor", {
                state: {
                  analysisResult,
                  parsedData:     analysisResult.parsedData,
                  jobRole:        selectedRole,
                  jobDescription,
                },
              });
            }
          }}
        />
      )}
    </div>
  );
}